import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SuperAdminLayout from "../components/SuperAdminLayout";
import api from "../services/api";

const STATUS_COLORS = {
  pending: "bg-yellow-500/10 text-yellow-400 border border-yellow-500/20",
  scheduled: "bg-blue-500/10 text-blue-400 border border-blue-500/20",
  ready_for_dispatch: "bg-purple-500/10 text-purple-400 border border-purple-500/20",
  assigned: "bg-blue-500/10 text-blue-400 border border-blue-500/20",
  picked: "bg-purple-500/10 text-purple-400 border border-purple-500/20",
  in_transit: "bg-orange-500/10 text-orange-400 border border-orange-500/20",
  attempted: "bg-amber-500/10 text-amber-400 border border-amber-500/20",
  delivered: "bg-green-500/10 text-green-400 border border-green-500/20",
  cancelled: "bg-red-500/10 text-red-400 border border-red-500/20",
};

export default function SuperAdminShopDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [shop, setShop] = useState(null);
  const [orders, setOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("orders");
  const [toast, setToast] = useState(null);

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [shopsRes, paymentsRes] = await Promise.all([
          api.get("/super-admin/shops"),
          api.get("/super-admin/payments"),
        ]);
        const found = shopsRes.data.find((s) => s.id === Number(id));
        if (!found) {
          navigate("/super-admin/shops");
          return;
        }
        setShop(found);
        setOrders(found.recent_orders || []);
        setRiders(found.riders || []);
        setPayments(paymentsRes.data.filter((p) => p.shop_id === Number(id)));
      } catch {
        showToast("Failed to load shop details.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [id, navigate, showToast]);

  const formatDate = (d) => d
    ? new Date(d).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" })
    : "—";

  const formatDateTime = (d) => d
    ? new Date(d).toLocaleString("en-KE", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";

  if (loading) return (
    <SuperAdminLayout>
      <div className="flex items-center justify-center h-64 text-slate-400 gap-3">
        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        Loading shop...
      </div>
    </SuperAdminLayout>
  );

  if (!shop) return null;

  const delivered = orders.filter((o) => o.status === "delivered").length;
  const active = orders.filter((o) => !["delivered", "cancelled"].includes(o.status)).length;
  const totalRev = payments.filter((p) => p.status === "paid").reduce((s, p) => s + Number(p.amount), 0);

  return (
    <SuperAdminLayout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl text-sm font-medium ${toast.type === "error" ? "bg-red-500 text-white" : "bg-green-500 text-white"
          }`}>
          {toast.type === "error" ? "❌" : "✅"} {toast.message}
        </div>
      )}

      <div className="p-7 space-y-6">

        {/* ── BACK + HEADER ── */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate("/super-admin/shops")}
            className="p-2 bg-slate-800 hover:bg-slate-700 rounded-xl border border-slate-700 transition-colors"
          >
            <svg className="w-4 h-4 text-slate-300" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white" style={{ fontFamily: "'Sora', sans-serif" }}>
              {shop.name}
            </h1>
            <p className="text-slate-400 text-sm">{shop.phone} · {shop.owner?.name}</p>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold capitalize ${shop.status === "active" ? "bg-green-500/10 text-green-400 border border-green-500/20" :
                shop.status === "trial" ? "bg-amber-500/10 text-amber-400 border border-amber-500/20" :
                  shop.status === "expired" ? "bg-red-500/10 text-red-400 border border-red-500/20" :
                    "bg-slate-500/10 text-slate-400 border border-slate-500/20"
              }`}>
              {shop.status}
            </span>
            <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-orange-500/10 text-orange-400 border border-orange-500/20 capitalize">
              {shop.plan}
            </span>
          </div>
        </div>

        {/* ── STATS ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: "Total Orders", value: orders.length, color: "text-white" },
            { label: "Active Orders", value: active, color: "text-orange-400" },
            { label: "Delivered", value: delivered, color: "text-green-400" },
            { label: "Total Revenue", value: `KES ${totalRev.toLocaleString()}`, color: "text-purple-400" },
          ].map((s) => (
            <div key={s.label} className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <p className="text-slate-400 text-xs mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: "'Sora', sans-serif" }}>
                {s.value}
              </p>
            </div>
          ))}
        </div>

        {/* ── SHOP INFO ── */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-widest mb-4">Shop Info</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
              { label: "Business Type", value: shop.business_type },
              { label: "Address", value: shop.address },
              { label: "Email", value: shop.email },
              { label: "KRA PIN", value: shop.kra_pin },
              { label: "Reg Number", value: shop.registration_number },
              { label: "Trial Ends", value: formatDate(shop.trial_ends_at) },
              { label: "Drivers", value: shop.driver_count },
              { label: "Plan", value: shop.plan },
              { label: "Active", value: shop.is_active ? "Yes" : "No" },
            ].map((r) => (
              <div key={r.label}>
                <p className="text-slate-500 text-xs mb-0.5">{r.label}</p>
                <p className="text-slate-200 text-sm font-medium">{r.value || "—"}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── TABS ── */}
        <div className="flex gap-2 bg-slate-900 border border-slate-800 rounded-xl p-1 w-fit">
          {["orders", "riders", "payments"].map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-4 py-2 rounded-lg text-xs font-semibold capitalize transition-all ${tab === t ? "bg-orange-500 text-white" : "text-slate-400 hover:text-white"
                }`}
            >
              {t} {t === "orders" ? `(${orders.length})` : t === "riders" ? `(${riders.length})` : `(${payments.length})`}
            </button>
          ))}
        </div>

        {/* ── ORDERS TAB ── */}
        {tab === "orders" && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
            {orders.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <p className="text-3xl mb-3">📦</p>
                <p className="font-medium">No orders yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800">
                      {["Token", "Customer", "Address", "Status", "Fee", "Date"].map((h) => (
                        <th key={h} className="text-left px-4 py-3.5 text-slate-400 font-semibold text-xs uppercase tracking-wide whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {orders.map((o) => (
                      <tr key={o.id} className="hover:bg-slate-800/40 transition-colors">
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs bg-slate-800 text-slate-300 px-2 py-1 rounded">
                            {o.tracking_token}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-slate-200 font-medium">{o.customer_name}</p>
                          <p className="text-slate-500 text-xs">{o.customer_phone}</p>
                        </td>
                        <td className="px-4 py-3 text-slate-400 text-xs max-w-[160px] truncate">
                          {o.address}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${STATUS_COLORS[o.status] || "bg-slate-500/10 text-slate-400"
                            }`}>
                            {o.status?.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-300 font-medium">
                          KES {Number(o.delivery_fee || 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                          {formatDateTime(o.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── RIDERS TAB ── */}
        {tab === "riders" && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
            {riders.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <p className="text-3xl mb-3">🏍️</p>
                <p className="font-medium">No riders yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800">
                      {["Name", "Phone", "Vehicle", "Status", "Commission"].map((h) => (
                        <th key={h} className="text-left px-4 py-3.5 text-slate-400 font-semibold text-xs uppercase tracking-wide whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {riders.map((r) => (
                      <tr key={r.id} className="hover:bg-slate-800/40 transition-colors">
                        <td className="px-4 py-3 text-slate-200 font-medium">{r.name}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs">{r.phone}</td>
                        <td className="px-4 py-3 text-slate-400 text-xs capitalize">{r.vehicle_type}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${r.is_active
                              ? "bg-green-500/10 text-green-400 border border-green-500/20"
                              : "bg-red-500/10 text-red-400 border border-red-500/20"
                            }`}>
                            {r.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-300 text-sm">
                          {r.commission_rate ? `${r.commission_rate}%` : "Shop default"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ── PAYMENTS TAB ── */}
        {tab === "payments" && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
            {payments.length === 0 ? (
              <div className="text-center py-16 text-slate-500">
                <p className="text-3xl mb-3">💳</p>
                <p className="font-medium">No payments yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800">
                      {["Amount", "Plan", "M-Pesa Ref", "Status", "Date"].map((h) => (
                        <th key={h} className="text-left px-4 py-3.5 text-slate-400 font-semibold text-xs uppercase tracking-wide whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {payments.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-800/40 transition-colors">
                        <td className="px-4 py-3 text-white font-bold">
                          KES {Number(p.amount).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-slate-300 capitalize">{p.plan}</td>
                        <td className="px-4 py-3">
                          <span className="font-mono text-xs text-slate-400">
                            {p.mpesa_ref || "—"}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-semibold ${p.status === "paid"
                              ? "bg-green-500/10 text-green-400 border border-green-500/20"
                              : "bg-red-500/10 text-red-400 border border-red-500/20"
                            }`}>
                            {p.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                          {formatDateTime(p.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
}